/* global Word */
import { RedlineSuggestion } from "../types";

const HIGHLIGHT_COLOR = "#FFEB3B";
const MAX_HIGHLIGHT_LEN = 240;

const trimForSearch = (text: string) => {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (!collapsed) {
    return "";
  }
  return collapsed.length > MAX_HIGHLIGHT_LEN ? collapsed.slice(0, MAX_HIGHLIGHT_LEN) : collapsed;
};

const stripHtmlToText = (html: string): string =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/(p|div|li|h[1-6]|tr|br)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const wordAdapter = {
  async getSelectedText() {
    return Word.run(async (context) => {
      const range = context.document.getSelection();
      range.load("text");
      await context.sync();
      return range.text || "";
    });
  },

  async getDocumentText() {
    return Word.run(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      return body.text || "";
    });
  },

  async insertAtCursor(content: string) {
    return Word.run(async (context) => {
      context.document.getSelection().insertText(content, Word.InsertLocation.replace);
      await context.sync();
    });
  },

  async appendToDocument(content: string) {
    return Word.run(async (context) => {
      context.document.body.insertParagraph(content, Word.InsertLocation.end);
      await context.sync();
    });
  },

  /**
   * Replace the entire document body with the given HTML/text.
   * If the input looks like HTML, we attempt insertHtml so basic formatting carries over;
   * otherwise we fall back to plain text paragraphs.
   */
  async replaceDocumentWithDraft(htmlOrText: string) {
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(htmlOrText);
    const cleanText = looksLikeHtml ? stripHtmlToText(htmlOrText) : htmlOrText.trim();
    if (!cleanText) {
      throw new Error("No content to insert.");
    }
    return Word.run(async (context) => {
      const body = context.document.body;
      body.clear();
      if (looksLikeHtml) {
        try {
          body.insertHtml(htmlOrText, Word.InsertLocation.start);
          await context.sync();
          return;
        } catch {
          // Fall through to plain-text insert if Word rejects the HTML.
        }
      }
      cleanText.split(/\n+/).forEach((paragraph, index) => {
        if (!paragraph.trim()) {
          return;
        }
        body.insertParagraph(paragraph, index === 0 ? Word.InsertLocation.start : Word.InsertLocation.end);
      });
      await context.sync();
    });
  },

  async insertDraftAtCursor(htmlOrText: string) {
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(htmlOrText);
    return Word.run(async (context) => {
      const range = context.document.getSelection();
      if (looksLikeHtml) {
        try {
          range.insertHtml(htmlOrText, Word.InsertLocation.replace);
          await context.sync();
          return;
        } catch {
          // fall through
        }
      }
      range.insertText(stripHtmlToText(htmlOrText), Word.InsertLocation.replace);
      await context.sync();
    });
  },

  async highlightExcerpt(excerpt: string) {
    const search = trimForSearch(excerpt);
    if (!search) {
      return false;
    }
    return Word.run(async (context) => {
      const body = context.document.body;
      const found = body.search(search, { matchCase: false, matchWholeWord: false });
      found.load("items");
      await context.sync();
      if (!found.items.length) {
        return false;
      }
      const target = found.items[0];
      target.font.highlightColor = HIGHLIGHT_COLOR;
      target.select(Word.SelectionMode.select);
      await context.sync();
      return true;
    });
  },

  async clearHighlights() {
    return Word.run(async (context) => {
      const body = context.document.body;
      body.font.highlightColor = "";
      await context.sync();
    });
  },

  async applySuggestion(suggestion: Pick<RedlineSuggestion, "originalText" | "suggestedText">) {
    return Word.run(async (context) => {
      // Enable tracked changes if the host supports it. Older Word builds (Word for the
      // web on some channels) don't expose changeTrackingMode; in that case we apply the
      // edit without tracking. Don't read the property — that requires load() + sync().
      try {
        (context.document as any).changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      } catch {
        // Tracked-changes not supported on this host build.
      }

      const search = trimForSearch(suggestion.originalText);
      const body = context.document.body;
      const found = body.search(search, {
        matchCase: false,
        matchWholeWord: false,
      });
      found.load("items");
      await context.sync();

      if (!found.items.length) {
        throw new Error("Original text for suggestion not found in document.");
      }

      found.items[0].insertText(suggestion.suggestedText, Word.InsertLocation.replace);
      await context.sync();
    });
  },
};
